import React from 'react';
import moment from 'moment';
import { isNil, isEmpty, pick } from 'lodash';
import { adjustmentTypes } from '../constants';
import { formatNumberWithCommas } from '../utils';

/**
 * The backend does not return Reversal States. For simplicity on the UI,
 * we have additional states for reversals.
 */
export const AdjustmentStatus = {
  Draft: 'Draft',
  ApprovalRequested: 'ApprovalRequested',
  ReviewRequested: 'ReviewRequested',
  Approved: 'Approved',
  Rejected: 'Rejected',
  ReversalDraft: 'ReversalDraft',
  ReversalApprovalRequested: 'ReversalApprovalRequested',
  ReversalApproved: 'ReversalApproved',
  ReversalRejected: 'ReversalRejected',
  AutoReversed: 'AutoReversed',
};

export const hasStatus = (adjustmentOrLineItem, expectedStatus) => {
  return adjustmentOrLineItem.status.toLowerCase() === expectedStatus.toLowerCase();
};

export const hasCalculatedUIStatus = (adjustmentOrLineItem, expectedStatus) => {
  return adjustmentOrLineItem.calculatedUIStatus.toLowerCase() === expectedStatus.toLowerCase();
};

export const hasReversalWithStatus = (adjustment, expectedStatus, givenAsLineItem) => {
  // in the case where adustment result is adjustment with lineitem unwinded, lineItemStatus
  if (givenAsLineItem) {
    const expandedLineItem = adjustment;
    return (
      !isNil(expandedLineItem.reversal) &&
      expandedLineItem.lineItemStatus.toLowerCase() === expectedStatus.toLowerCase()
    );
  }
  return adjustment.lineItems?.some(
    lineItem => !isNil(lineItem.reversal) && hasStatus(lineItem, expectedStatus),
  );
};

export const getCalculatedAdjustmentStatus = (adjustment, givenAsLineItem) => {
  if (hasStatus(adjustment, AdjustmentStatus.Draft)) {
    if (!isNil(adjustment.rejectReason)) {
      return AdjustmentStatus.Rejected;
    }
  }

  if (hasStatus(adjustment, AdjustmentStatus.Approved)) {
    if (hasReversalWithStatus(adjustment, AdjustmentStatus.Draft, givenAsLineItem)) {
      if (!isNil(adjustment.rejectReason)) {
        return AdjustmentStatus.ReversalRejected;
      }

      return AdjustmentStatus.ReversalDraft;
    }

    if (hasReversalWithStatus(adjustment, AdjustmentStatus.ApprovalRequested, givenAsLineItem)) {
      return AdjustmentStatus.ReversalApprovalRequested;
    }

    if (hasReversalWithStatus(adjustment, AdjustmentStatus.Approved, givenAsLineItem)) {
      if (adjustment.autoReverse) {
        return AdjustmentStatus.AutoReversed;
      }

      return AdjustmentStatus.ReversalApproved;
    }
  }

  return adjustment.status;
};

const calculateLineItemReversalStatus = (lineItem, adjustment) => {
  let calculatedUIStatus = lineItem.status;

  if (hasStatus(lineItem, AdjustmentStatus.Draft)) {
    if (!isNil(adjustment.rejectReason)) {
      calculatedUIStatus = AdjustmentStatus.ReversalRejected;
    } else {
      calculatedUIStatus = AdjustmentStatus.ReversalDraft;
    }
  }

  if (hasStatus(lineItem, AdjustmentStatus.ApprovalRequested)) {
    calculatedUIStatus = AdjustmentStatus.ReversalApprovalRequested;
  }

  if (hasStatus(lineItem, AdjustmentStatus.Approved)) {
    if (adjustment.autoReverse) {
      calculatedUIStatus = AdjustmentStatus.AutoReversed;
    } else {
      calculatedUIStatus = AdjustmentStatus.ReversalApproved;
    }
  }

  return calculatedUIStatus;
};

export const calculateLineItemStatus = adjustment => {
  return adjustment.lineItems?.map(lineItem => {
    let calculatedUIStatus = lineItem.status;

    if (lineItem.reversal) {
      calculatedUIStatus = calculateLineItemReversalStatus(lineItem, adjustment);
    } else {
      const foundReversalLineItem = adjustment.lineItems.find(
        li => li.reversal && li.reversal.lineItemId === lineItem._id,
      );

      if (foundReversalLineItem) {
        calculatedUIStatus = calculateLineItemReversalStatus(foundReversalLineItem, adjustment);
      } else if (lineItem.status === AdjustmentStatus.Draft && !isNil(adjustment.rejectReason)) {
        calculatedUIStatus = AdjustmentStatus.Rejected;
      }
    }

    return {
      ...lineItem,
      calculatedUIStatus,
    };
  });
};

export const getDisplayableLineItemChangesForReversals = adjustment => {
  const lineItemChanges = adjustment.lineItems
    .filter(lineItem => !isNil(lineItem.reversal))
    .filter(lineItem => hasCalculatedUIStatus(lineItem, AdjustmentStatus.ReversalApprovalRequested))
    .map(reversedLineItem => {
      const originalLineItem = adjustment.lineItems.find(
        lineItem => lineItem._id === reversedLineItem.reversal.lineItemId,
      );

      const { _id } = reversedLineItem;

      const title =
        reversedLineItem.productDetails?.name || reversedLineItem.pfamDetails?.productFamilyName;

      const changes = [];

      if (reversedLineItem.amount !== originalLineItem.amount) {
        changes.push(
          `Amount updated from ${originalLineItem.amount} to ${reversedLineItem.amount}`,
        );
      }

      if (reversedLineItem.actualDate !== originalLineItem.actualDate) {
        changes.push(
          `Actual Date updated from ${new Date(
            originalLineItem.actualDate,
          ).toLocaleDateString()} to ${new Date(reversedLineItem.actualDate).toLocaleDateString()}`,
        );
      }

      return {
        _id,
        title,
        changes,
      };
    });

  return lineItemChanges;
};

export const getTotalPayment = payments => {
  return payments.reduce((sum, payment) => sum + parseFloat(payment), 0);
};

export const getNumberValueFromString = value => {
  // eslint-disable-next-line no-nested-ternary
  return typeof value === 'string' ? (isEmpty(value) ? 0 : Number(value.replace(/,/g, ''))) : value;
};

export const getPaymentAmountNumber = payment => {
  return getNumberValueFromString(payment.amount);
};

export const getAdvancePaymentTotalString = (payments, styles, returnString = false) => {
  if (!payments) {
    return '';
  }
  const currency = payments[0]?.currency?.code || payments[0]?.currency;
  if (payments.some(payment => (payment.currency?.code || payment.currency) !== currency)) {
    return '';
  }
  const total = getTotalPayment(payments.map(getPaymentAmountNumber));

  if (returnString) {
    return `${formatNumberWithCommas(total.toFixed(2))} ${currency}`;
  }

  if (total < 0) {
    return (
      <h3>
        Total Payment:{' '}
        <span className={styles && styles.negativeAmount}>
          ({formatNumberWithCommas(Math.abs(total).toFixed(2))} {currency})
        </span>
      </h3>
    );
  }

  return (
    <h3>
      Total Payment: {formatNumberWithCommas(total.toFixed(2))} {currency}
    </h3>
  );
};

export const getAdjustmentListItemsName = seletedAdjustmentType => {
  const listItemsName = adjustmentTypes.find(l => l.key === seletedAdjustmentType)?.listItemsName;
  return listItemsName;
};

export const formatDate = date => (date ? moment.utc(date).format('YYYY-MM-DD') : undefined);
const formatEndOfMonthDate = date => (date ? moment.utc(date).endOf('month').format() : undefined);

export const getLineItems = (values, type) => {
  switch (type) {
    case 'AdjustmentToRoyaltiesEarned':
      return values.adjustments.map(lineItem => ({
        ...pick(lineItem, ['amount', 'product', 'billToGeography', 'geography']),
        currency: lineItem.currency?.code || lineItem.currency,
        actualDate: formatDate(lineItem.actualDate),
        product: lineItem.product?.key || undefined,
        amount: getNumberValueFromString(lineItem.amount),
      }));
    case 'RecoupablePayment':
      return values.recoupablePayments.map(lineItem => ({
        ...pick(lineItem, [
          'invoiceNumber',
          'amount',
          'invoiceDate',
          'statementPeriod',
          'dueDate',
          'taxCode',
          'statementComment',
          'description',
        ]),
        currency: lineItem.currency?.code || lineItem.currency,
        invoiceDate: formatDate(lineItem.invoiceDate),
        dueDate: formatDate(lineItem.dueDate),
        statementPeriod: formatEndOfMonthDate(lineItem.statementPeriod),
        product: lineItem.product?.key || undefined,
        amount: getNumberValueFromString(lineItem.amount),
        invoiceApproverId: lineItem.invoiceApprover?.oid,
        interimInvoiceApproverId: lineItem.interimInvoiceApprover?.oid,
      }));
    case 'NonRecoupablePayment':
      return values.nonRecoupablePayments.map(lineItem => ({
        ...pick(lineItem, [
          'invoiceNumber',
          'amount',
          'invoiceDate',
          'dueDate',
          'taxCode',
          'expenseAccount',
          'costCenter',
          'description',
        ]),
        currency: lineItem.currency?.code || lineItem.currency,
        invoiceDate: formatDate(lineItem.invoiceDate),
        dueDate: formatDate(lineItem.dueDate),
        product: lineItem.product?.key || undefined,
        amount: getNumberValueFromString(lineItem.amount),
        invoiceApproverId: lineItem.invoiceApprover?.oid,
        interimInvoiceApproverId: lineItem.interimInvoiceApprover?.oid,
      }));
    case 'AdvanceBalance':
      return values.advanceBalanceAdjustments.map(lineItem => ({
        ...pick(lineItem, [
          'amount',
          'adjustmentDate',
          'statementPeriod',
          'statementComment',
          'description',
        ]),
        adjustmentDate: formatDate(lineItem.adjustmentDate),
        statementPeriod: formatEndOfMonthDate(lineItem.statementPeriod),
        currency: lineItem.currency?.code || lineItem.currency,
        product: lineItem.product?.key || undefined,
        amount: getNumberValueFromString(lineItem.amount),
      }));
    default:
      return {};
  }
};
